import { Add, Close, Information } from '@carbon/icons-react';
import * as React from 'react';
import {
  Button,
  Heading,
  Icon,
  Input,
  Selector,
  Toggle,
  Tooltip,
} from 'vet-bones/design-system/components';
import { SelectItem, createItem } from 'vet-bones/design-system/utils/select';

import { TypeDescriptionCombinedOption } from 'src/constants/dataSourceTypeDescription';
import {
  TEST_IDS,
  getVirtualGraphAdvancedOptionPropertyTestId,
  getVirtualGraphAdvancedOptionRemoveTestId,
  getVirtualGraphAdvancedOptionValueTestId,
} from 'src/constants/testIds';
import * as copy from 'src/templates/copy';

import styles from 'src/containers/ProjectVirtualGraphPreviewDialog/components/advancedOptions.module.scss';

export type AdvancedOption = {
  property: string;
  value: string;
};

type PropertyMetadata = TypeDescriptionCombinedOption;

type AdvancedOptionsProps = {
  options: AdvancedOption[];
  onOptionsChange: (options: AdvancedOption[]) => void;
  propertySuggestions: string[];
  propertyMetadata: Record<string, PropertyMetadata>;
  disabled?: boolean;
};

const getPropertyType = (
  property: string,
  metadata: Record<string, PropertyMetadata>
): 'string' | 'integer' | 'boolean' => {
  return metadata[property]?.type || 'string';
};

const getPropertyDescription = (
  property: string,
  metadata: Record<string, PropertyMetadata>
): string | undefined => {
  return metadata[property]?.description;
};

const getPropertyExamples = (
  property: string,
  metadata: Record<string, PropertyMetadata>
): Array<string | number | boolean> | undefined => {
  return metadata[property]?.examples;
};

const getPropertyDefault = (
  property: string,
  metadata: Record<string, PropertyMetadata>
): string | number | boolean | undefined => {
  return metadata[property]?.default;
};

const ValueInput: React.VFC<{
  index: number;
  disabled?: boolean;
  handleBooleanToggle: (index: number) => void;
  handleValueChange: (index: number, value: string) => void;
  handleValueSelect: (index: number, item: SelectItem | null) => void;
  option: AdvancedOption;
  propertyMetadata: Record<string, PropertyMetadata>;
}> = ({
  index,
  disabled,
  handleBooleanToggle,
  handleValueChange,
  handleValueSelect,
  option,
  propertyMetadata,
}) => {
  const propertyType = getPropertyType(option.property, propertyMetadata);
  const examples = getPropertyExamples(option.property, propertyMetadata);
  const defaultValue = getPropertyDefault(option.property, propertyMetadata);

  if (propertyType === 'boolean') {
    return (
      <div className={styles.booleanToggle}>
        <Toggle
          checked={option.value === 'true'}
          disabled={disabled}
          id={`advanced-option-value-${index}`}
          onChange={() => handleBooleanToggle(index)}
          size="md"
          testId={getVirtualGraphAdvancedOptionValueTestId(index)}
        />
        <span className={styles.booleanLabel}>
          {option.value === 'true'
            ? copy.components.virtualGraphPreviewDialog.advancedOptions
                .booleanTrue
            : copy.components.virtualGraphPreviewDialog.advancedOptions
                .booleanFalse}
        </span>
      </div>
    );
  }

  if (propertyType === 'integer') {
    return (
      <Input
        aria-label={
          copy.components.virtualGraphPreviewDialog.advancedOptions
            .propertyValue
        }
        clearable={false}
        disabled={disabled}
        id={`advanced-option-value-${index}`}
        label=""
        onChange={(e) => {
          const numericValue = e.target.value.replace(/[^0-9-]/g, '');
          handleValueChange(index, numericValue);
        }}
        placeholder={
          defaultValue !== undefined ? String(defaultValue) : undefined
        }
        size="md"
        testId={getVirtualGraphAdvancedOptionValueTestId(index)}
        type="number"
        value={option.value}
      />
    );
  }

  const hasExamples = examples && examples.length > 0;

  if (hasExamples) {
    const exampleItems = examples.map((ex) =>
      createItem(String(ex), String(ex))
    );
    const selectedItem = option.value
      ? exampleItems.find((item) => item.value === option.value) ||
        createItem(option.value, option.value)
      : null;

    return (
      <Selector.Single
        creatable
        createNewItemFromQuery={(query) => createItem(query, query)}
        createText={(query) =>
          copy.components.defaults.itemSelectorCreate(query)
        }
        disabled={disabled}
        filterPlaceholder={copy.components.defaults.filter}
        inputProps={{
          id: `advanced-option-value-${index}`,
          label: '',
          placeholder:
            copy.components.virtualGraphPreviewDialog.advancedOptions
              .selectValue,
          size: 'md',
          testId: getVirtualGraphAdvancedOptionValueTestId(index),
          value: option.value || '',
        }}
        items={exampleItems}
        onSelect={(item) => handleValueSelect(index, item)}
        selected={selectedItem}
      />
    );
  }

  return (
    <Input
      aria-label={
        copy.components.virtualGraphPreviewDialog.advancedOptions.propertyValue
      }
      clearable={false}
      disabled={disabled}
      id={`advanced-option-value-${index}`}
      label=""
      onChange={(e) => handleValueChange(index, e.target.value)}
      placeholder={
        defaultValue !== undefined ? String(defaultValue) : undefined
      }
      size="md"
      testId={getVirtualGraphAdvancedOptionValueTestId(index)}
      value={option.value}
    />
  );
};

export const AdvancedOptions: React.VFC<AdvancedOptionsProps> = ({
  options,
  onOptionsChange,
  propertySuggestions,
  propertyMetadata,
  disabled,
}) => {
  const propertySuggestionItems = React.useMemo(
    () => propertySuggestions.map((s) => createItem(s, s)),
    [propertySuggestions]
  );

  const handlePropertyChange = (index: number, property: string) => {
    const newOptions = [...options];
    const propertyType = getPropertyType(property, propertyMetadata);
    const defaultValue = getPropertyDefault(property, propertyMetadata);

    let newValue = newOptions[index].value;
    if (property !== newOptions[index].property) {
      if (propertyType === 'boolean') {
        newValue = defaultValue !== undefined ? String(defaultValue) : 'false';
      } else {
        newValue = '';
      }
    }

    newOptions[index] = {
      ...newOptions[index],
      property,
      value: newValue,
    };
    onOptionsChange(newOptions);
  };

  const handlePropertySelect = (index: number, item: SelectItem | null) => {
    if (item) {
      handlePropertyChange(index, item.value);
    }
  };

  const handleValueChange = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = {
      ...newOptions[index],
      value,
    };
    onOptionsChange(newOptions);
  };

  const handleValueSelect = (index: number, item: SelectItem | null) => {
    if (item) {
      handleValueChange(index, item.value);
    }
  };

  const handleBooleanToggle = (index: number) => {
    const newOptions = [...options];
    const currentValue = newOptions[index].value === 'true';
    newOptions[index] = {
      ...newOptions[index],
      value: String(!currentValue),
    };
    onOptionsChange(newOptions);
  };

  const handleRemoveOption = (index: number) => {
    const filtered = options.filter((_, i) => i !== index);
    // Always keep at least one empty row
    onOptionsChange(
      filtered.length > 0 ? filtered : [{ property: '', value: '' }]
    );
  };

  const isLastOptionEmpty = React.useMemo(() => {
    const lastOption = options[options.length - 1];
    return lastOption && lastOption.property === '' && lastOption.value === '';
  }, [options]);

  const handleAddOption = () => {
    if (isLastOptionEmpty) {
      return;
    }
    onOptionsChange([...options, { property: '', value: '' }]);
  };

  return (
    <div className={styles.advancedOptionsContent}>
      <div className={styles.advancedOptionsHeader}>
        <Heading className={styles.advancedOptionsColumn} size="sm">
          {
            copy.components.virtualGraphPreviewDialog.advancedOptions
              .propertyHeader
          }
        </Heading>
        <Heading className={styles.advancedOptionsColumn} size="sm">
          {
            copy.components.virtualGraphPreviewDialog.advancedOptions
              .valueHeader
          }
        </Heading>
        <div className={styles.advancedOptionsActionsColumn} />
      </div>
      {options.map((option, index) => {
        const rowId = `advanced-option-${option.property || 'new'}-${index}`;
        const description = getPropertyDescription(
          option.property,
          propertyMetadata
        );

        // Find selected property item for the selector
        const selectedPropertyItem = option.property
          ? propertySuggestionItems.find(
              (item) => item.value === option.property
            ) || createItem(option.property, option.property)
          : null;

        return (
          <div key={rowId} className={styles.advancedOptionsRow}>
            <div className={styles.advancedOptionsColumn}>
              <div className={styles.propertyInputWrapper}>
                <Selector.Single
                  creatable
                  createNewItemFromQuery={(query) => createItem(query, query)}
                  createText={(query) =>
                    copy.components.defaults.itemSelectorCreate(query)
                  }
                  disabled={disabled}
                  filterPlaceholder={copy.components.defaults.filter}
                  inputProps={{
                    id: `advanced-option-property-${index}`,
                    label: '',
                    placeholder:
                      copy.components.virtualGraphPreviewDialog.advancedOptions
                        .selectProperty,
                    size: 'md',
                    testId: getVirtualGraphAdvancedOptionPropertyTestId(index),
                    value: option.property || '',
                  }}
                  items={propertySuggestionItems}
                  onSelect={(item) => handlePropertySelect(index, item)}
                  selected={selectedPropertyItem}
                />
                {description ? (
                  <Tooltip description={description} placement="bottom">
                    <Icon
                      className={styles.infoIcon}
                      IconComponent={Information}
                      size="sm"
                    />
                  </Tooltip>
                ) : null}
              </div>
            </div>
            <div className={styles.advancedOptionsColumn}>
              <ValueInput
                disabled={disabled}
                handleBooleanToggle={handleBooleanToggle}
                handleValueChange={handleValueChange}
                handleValueSelect={handleValueSelect}
                index={index}
                option={option}
                propertyMetadata={propertyMetadata}
              />
            </div>
            <div
              className={styles.advancedOptionsActionsColumn}
              data-testid={getVirtualGraphAdvancedOptionRemoveTestId(index)}
            >
              <Button
                disabled={disabled}
                icon={Close}
                onClick={() => handleRemoveOption(index)}
                size="sm"
                variant="ghost-gray"
              />
            </div>
          </div>
        );
      })}
      <Button
        disabled={disabled || isLastOptionEmpty}
        icon={Add}
        onClick={handleAddOption}
        size="sm"
        testId={
          TEST_IDS.projectVirtualGraphPanel.resourceProperties.advancedOptions
            .addButton
        }
        variant="secondary-solid-gray"
      >
        {copy.components.virtualGraphPreviewDialog.advancedOptions.addOption}
      </Button>
    </div>
  );
};
